import React, { useState, useEffect, useMemo } from 'react';
import { Trash2, Copy, Download, Shuffle, Filter, Eye, Settings, RotateCcw, CheckCircle } from 'lucide-react';

interface RemovalMethod {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface ProcessingOptions {
  caseSensitive: boolean;
  trimWhitespace: boolean;
  ignoreEmptyLines: boolean;
  keepFirstOccurrence: boolean;
  sortResult: boolean;
  reverseSort: boolean;
  minimumLineLength: number;
  maximumLineLength: number;
  preserveLineNumbers: boolean;
  highlightRemovedCount: boolean;
}

interface ProcessingResult {
  originalLines: string[];
  processedLines: string[];
  duplicatesFound: string[];
  duplicateCount: number;
  removedCount: number;
  keptCount: number;
  emptyLinesRemoved: number;
  statistics: {
    originalTotal: number;
    finalTotal: number;
    uniqueLines: number;
    duplicateGroups: number;
  };
}

const RemoveDuplicateLines: React.FC = () => {
  const [input, setInput] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('exact');
  const [result, setResult] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [options, setOptions] = useState<ProcessingOptions>({
    caseSensitive: true,
    trimWhitespace: true,
    ignoreEmptyLines: true,
    keepFirstOccurrence: true,
    sortResult: false,
    reverseSort: false,
    minimumLineLength: 0,
    maximumLineLength: 1000,
    preserveLineNumbers: false,
    highlightRemovedCount: true
  });

  const removalMethods: RemovalMethod[] = [
    {
      id: 'exact',
      name: 'Exact Match',
      description: 'Remove lines that match exactly',
      icon: <CheckCircle className="w-4 h-4" />,
      color: 'blue'
    },
    {
      id: 'caseInsensitive',
      name: 'Case Insensitive',
      description: 'Ignore case when comparing lines',
      icon: <Filter className="w-4 h-4" />,
      color: 'green'
    },
    {
      id: 'trimmed',
      name: 'Trimmed Comparison',
      description: 'Compare after removing leading/trailing spaces',
      icon: <Settings className="w-4 h-4" />,
      color: 'purple'
    },
    {
      id: 'normalized',
      name: 'Normalized Text',
      description: 'Remove extra spaces and normalize whitespace',
      icon: <Filter className="w-4 h-4" />,
      color: 'red'
    },
    {
      id: 'alphabetic',
      name: 'Alphabetic Only',
      description: 'Compare only alphabetic characters',
      icon: <Eye className="w-4 h-4" />,
      color: 'yellow'
    },
    {
      id: 'alphanumeric',
      name: 'Alphanumeric Only',
      description: 'Compare only letters and numbers',
      icon: <Eye className="w-4 h-4" />,
      color: 'indigo'
    },
    {
      id: 'similarWords',
      name: 'Similar Word Order',
      description: 'Lines with same words in any order',
      icon: <RotateCcw className="w-4 h-4" />,
      color: 'pink'
    },
    {
      id: 'startsWith',
      name: 'Same Starting Words',
      description: 'Lines starting with same first 3 words',
      icon: <Filter className="w-4 h-4" />,
      color: 'orange'
    },
    {
      id: 'contains',
      name: 'Contains Substring',
      description: 'Lines containing the same key phrases',
      icon: <Eye className="w-4 h-4" />,
      color: 'teal'
    },
    {
      id: 'length',
      name: 'Same Length',
      description: 'Remove lines with identical character count',
      icon: <Settings className="w-4 h-4" />,
      color: 'cyan'
    },
    {
      id: 'pattern',
      name: 'Pattern Matching',
      description: 'Lines matching similar patterns (emails, URLs, etc.)',
      icon: <Filter className="w-4 h-4" />,
      color: 'emerald'
    },
    {
      id: 'fuzzy',
      name: 'Fuzzy Matching',
      description: 'Lines that are very similar (90%+ match)',
      icon: <Eye className="w-4 h-4" />,
      color: 'violet'
    }
  ];

  const processLines = (lines: string[], method: string, opts: ProcessingOptions): ProcessingResult => {
    let processedLines = [...lines];
    let originalLines = [...lines];
    
    // Apply basic filtering
    if (opts.ignoreEmptyLines) {
      processedLines = processedLines.filter(line => line.trim() !== '');
    }
    
    if (opts.trimWhitespace) {
      processedLines = processedLines.map(line => line.trim());
    }

    // Apply length filters
    processedLines = processedLines.filter(line => 
      line.length >= opts.minimumLineLength && line.length <= opts.maximumLineLength
    );

    // Generate comparison keys based on method
    const getComparisonKey = (line: string): string => {
      switch (method) {
        case 'exact':
          return opts.caseSensitive ? line : line.toLowerCase();
        case 'caseInsensitive':
          return line.toLowerCase();
        case 'trimmed':
          return opts.caseSensitive ? line.trim() : line.trim().toLowerCase();
        case 'normalized':
          return line.replace(/\s+/g, ' ').trim().toLowerCase();
        case 'alphabetic':
          return line.replace(/[^a-zA-Z]/g, '').toLowerCase();
        case 'alphanumeric':
          return line.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        case 'similarWords':
          return line.toLowerCase().split(/\s+/).sort().join(' ');
        case 'startsWith':
          return line.toLowerCase().split(/\s+/).slice(0, 3).join(' ');
        case 'contains':
          return line.toLowerCase().replace(/[^\w\s]/g, '');
        case 'length':
          return line.length.toString();
        case 'pattern':
          // Detect common patterns
          if (/@/.test(line)) return 'email';
          if (/https?:\/\//.test(line)) return 'url';
          if (/\d{3}-\d{3}-\d{4}/.test(line)) return 'phone';
          return line.toLowerCase();
        case 'fuzzy':
          return line.toLowerCase().replace(/[^\w]/g, '');
        default:
          return line;
      }
    };

    // Track seen lines and their indices
    const seen = new Map<string, number>();
    const duplicates: string[] = [];
    const uniqueLines: string[] = [];
    let duplicateCount = 0;

    processedLines.forEach((line, index) => {
      const key = getComparisonKey(line);
      
      if (seen.has(key)) {
        duplicates.push(line);
        duplicateCount++;
        if (!opts.keepFirstOccurrence) {
          // Remove first occurrence and keep this one
          const firstIndex = seen.get(key)!;
          uniqueLines[firstIndex] = line;
        }
      } else {
        seen.set(key, uniqueLines.length);
        uniqueLines.push(line);
      }
    });

    // Apply sorting if requested
    let finalLines = uniqueLines;
    if (opts.sortResult) {
      finalLines = [...uniqueLines].sort();
      if (opts.reverseSort) {
        finalLines.reverse();
      }
    }

    // Add line numbers if requested
    if (opts.preserveLineNumbers) {
      finalLines = finalLines.map((line, index) => `${index + 1}. ${line}`);
    }

    return {
      originalLines,
      processedLines: finalLines,
      duplicatesFound: duplicates,
      duplicateCount,
      removedCount: processedLines.length - uniqueLines.length,
      keptCount: uniqueLines.length,
      emptyLinesRemoved: originalLines.length - processedLines.length,
      statistics: {
        originalTotal: originalLines.length,
        finalTotal: finalLines.length,
        uniqueLines: uniqueLines.length,
        duplicateGroups: seen.size
      }
    };
  };

  const processingResult: ProcessingResult = useMemo(() => {
    if (!input.trim()) {
      return {
        originalLines: [],
        processedLines: [],
        duplicatesFound: [],
        duplicateCount: 0,
        removedCount: 0,
        keptCount: 0,
        emptyLinesRemoved: 0,
        statistics: {
          originalTotal: 0,
          finalTotal: 0,
          uniqueLines: 0,
          duplicateGroups: 0
        }
      };
    }

    const lines = input.split('\n');
    return processLines(lines, selectedMethod, options);
  }, [input, selectedMethod, options]);

  useEffect(() => {
    setResult(processingResult.processedLines.join('\n'));
  }, [processingResult]);

  const copyToClipboard = async () => {
    if (result) {
      await navigator.clipboard.writeText(result);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const downloadResult = () => {
    if (result) {
      const stats = `Processing Summary:
Original lines: ${processingResult.statistics.originalTotal}
Final lines: ${processingResult.statistics.finalTotal}
Duplicates removed: ${processingResult.removedCount}
Method used: ${removalMethods.find(m => m.id === selectedMethod)?.name}

Processed Text:
${result}`;

      const blob = new Blob([stats], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'deduplicated-lines.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const generateSampleText = () => {
    const samples = [
      `apple
banana
cherry
apple
date
banana
elderberry
fig
cherry
grape
apple
honeydew`,
      `john@example.com
Jane Smith
john@example.com
jane smith
Bob Johnson
JANE SMITH
alice@test.com
Bob Johnson
charlie@demo.org
jane smith`,
      `Line 1: Important data
Line 2: Another entry
  Line 1: Important data  
Line 3: Different content
LINE 1: IMPORTANT DATA
Line 4: Final entry
Line 2: Another entry`,
      `TODO: Fix the bug
DONE: Complete feature
TODO: Write documentation
todo: fix the bug
IN PROGRESS: Code review
TODO: Write tests
DONE: Complete feature
TODO: Deploy to production`
    ];
    setInput(samples[Math.floor(Math.random() * samples.length)]);
  };

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
      pink: 'bg-pink-50 border-pink-200 text-pink-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
      cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      violet: 'bg-violet-50 border-violet-200 text-violet-800'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-3">
            <Trash2 className="text-red-600" />
            Advanced Duplicate Line Remover
          </h1>
          <p className="text-gray-600">Powerful duplicate detection with multiple comparison methods</p>
        </div>

        {/* Input Section */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <label className="text-lg font-semibold text-gray-700">Input Text</label>
            <div className="flex gap-2">
              <button
                onClick={generateSampleText}
                className="flex items-center gap-2 px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
              >
                <Shuffle size={16} />
                Sample Text
              </button>
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
              >
                <Settings size={16} />
                {showOptions ? 'Hide' : 'Show'} Options
              </button>
              <button
                onClick={() => setShowStatistics(!showStatistics)}
                className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
              >
                <Eye size={16} />
                Statistics
              </button>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Enter your text with duplicate lines here..."
                className="w-full h-40 p-4 border-2 border-gray-200 rounded-xl focus:border-red-500 focus:outline-none resize-none text-gray-700 transition-colors"
              />
              <div className="text-sm text-gray-500 mt-2">
                Lines: {input ? input.split('\n').length : 0}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700">Result</label>
                <div className="flex gap-2">
                  <button
                    onClick={copyToClipboard}
                    disabled={!result}
                    className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Copy size={16} />
                    {copySuccess ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={downloadResult}
                    disabled={!result}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Download size={16} />
                    Download
                  </button>
                </div>
              </div>
              <textarea
                value={result}
                readOnly
                className="w-full h-40 p-4 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-700 resize-none"
                placeholder="Processed text will appear here..."
              />
              <div className="text-sm text-gray-500 mt-2">
                Lines: {result ? result.split('\n').length : 0} | Removed: {processingResult.removedCount}
              </div>
            </div>
          </div>
        </div>

        {/* Processing Options */}
        {showOptions && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-800">Processing Options</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Comparison Settings</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.caseSensitive}
                    onChange={(e) => setOptions({...options, caseSensitive: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Case Sensitive</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.trimWhitespace}
                    onChange={(e) => setOptions({...options, trimWhitespace: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Trim Whitespace</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.ignoreEmptyLines}
                    onChange={(e) => setOptions({...options, ignoreEmptyLines: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Ignore Empty Lines</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Output Options</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.keepFirstOccurrence}
                    onChange={(e) => setOptions({...options, keepFirstOccurrence: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Keep First Occurrence</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.sortResult}
                    onChange={(e) => setOptions({...options, sortResult: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Sort Result</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.reverseSort}
                    onChange={(e) => setOptions({...options, reverseSort: e.target.checked})}
                    disabled={!options.sortResult}
                    className="w-4 h-4 text-purple-600 disabled:opacity-50"
                  />
                  <span className="text-sm">Reverse Sort</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.preserveLineNumbers}
                    onChange={(e) => setOptions({...options, preserveLineNumbers: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Add Line Numbers</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Length Filters</h3>
                
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Minimum Line Length</label>
                  <input
                    type="number"
                    min="0"
                    value={options.minimumLineLength}
                    onChange={(e) => setOptions({...options, minimumLineLength: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Maximum Line Length</label>
                  <input
                    type="number"
                    min="1"
                    value={options.maximumLineLength}
                    onChange={(e) => setOptions({...options, maximumLineLength: parseInt(e.target.value) || 1000})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Removal Methods */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Filter className="text-red-600" />
            <h2 className="text-xl font-semibold text-gray-800">Choose Duplicate Detection Method</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {removalMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => setSelectedMethod(method.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  selectedMethod === method.id
                    ? `border-${method.color}-500 ${getColorClasses(method.color)} shadow-md`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {method.icon}
                    <h3 className={`font-semibold ${
                      selectedMethod === method.id ? '' : 'text-gray-800'
                    }`}>
                      {method.name}
                    </h3>
                  </div>
                  {selectedMethod === method.id && (
                    <div className={`w-3 h-3 bg-${method.color}-500 rounded-full`}></div>
                  )}
                </div>
                <p className={`text-sm ${
                  selectedMethod === method.id ? '' : 'text-gray-600'
                }`}>
                  {method.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Statistics */}
        {showStatistics && input && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <Eye className="text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-800">Processing Statistics</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Original</h3>
                <div className="text-2xl font-bold text-blue-900">{processingResult.statistics.originalTotal}</div>
                <p className="text-sm text-blue-600">Total lines</p>
              </div>

              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Remaining</h3>
                <div className="text-2xl font-bold text-green-900">{processingResult.statistics.finalTotal}</div>
                <p className="text-sm text-green-600">Lines kept</p>
              </div>

              <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                <h3 className="font-semibold text-red-800 mb-2">Removed</h3>
                <div className="text-2xl font-bold text-red-900">{processingResult.removedCount}</div>
                <p className="text-sm text-red-600">Duplicates found</p>
              </div>

              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Efficiency</h3>
                <div className="text-2xl font-bold text-purple-900">
                  {processingResult.statistics.originalTotal > 0 
                    ? Math.round((processingResult.removedCount / processingResult.statistics.originalTotal) * 100)
                    : 0}%
                </div>
                <p className="text-sm text-purple-600">Duplicates removed</p>
              </div>
            </div>

            {processingResult.duplicatesFound.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-700 mb-3">Sample Duplicates Found:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {processingResult.duplicatesFound.slice(0, 6).map((duplicate, index) => (
                    <div key={index} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="text-sm text-red-800 font-mono truncate">{duplicate}</div>
                    </div>
                  ))}
                </div>
                {processingResult.duplicatesFound.length > 6 && (
                  <p className="text-sm text-gray-600 mt-2">
                    ...and {processingResult.duplicatesFound.length - 6} more duplicates
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RemoveDuplicateLines;